import { useState } from 'react'
import { Check, Copy, FileSpreadsheet, Link2, Upload } from 'lucide-react'
import { Button } from '../ui/Button'
import { Input } from '../ui/Input'
import { Modal } from '../ui/Modal'
import { useUIStore } from '../../stores/uiStore'

export function ShortcutModals() {
  const openModal = useUIStore((s) => s.openModal)
  const setOpenModal = useUIStore((s) => s.setOpenModal)

  return (
    <>
      <RecordVisitModal
        open={openModal === 'record-visit'}
        onClose={() => setOpenModal(null)}
      />
      <TrackingLinkModal
        open={openModal === 'tracking-link'}
        onClose={() => setOpenModal(null)}
      />
      <UploadDataModal
        open={openModal === 'upload-data'}
        onClose={() => setOpenModal(null)}
      />
    </>
  )
}

// ─── Record visit ──────────────────────────────────────────────────
function RecordVisitModal({ open, onClose }: { open: boolean; onClose: () => void }) {
  return (
    <Modal
      open={open}
      onClose={onClose}
      title="บันทึกการใช้บริการ"
      size="md"
      footer={
        <>
          <Button variant="glass-dark" onClick={onClose}>ยกเลิก</Button>
          <Button onClick={onClose}>บันทึก</Button>
        </>
      }
    >
      <div className="flex flex-col gap-4">
        <Input label="HN / เบอร์โทรลูกค้า" placeholder="เช่น HN12345 หรือ 0812345678" />
        <Input label="วันที่ใช้บริการ" type="date" />
        <Input label="บริการ" placeholder="เช่น ตรวจสุขภาพหัวใจ" />
        <Input label="มูลค่า (THB)" type="number" placeholder="0" />
        <label className="flex flex-col gap-2">
          <span className="font-450 text-body-sm text-black/70">หมายเหตุ</span>
          <textarea
            className="rounded-lg border border-black/[0.08] bg-white px-3 py-2 text-body font-330 outline-none focus:border-black/40 transition-colors"
            rows={3}
            placeholder="ข้อมูลเพิ่มเติม…"
          />
        </label>
      </div>
    </Modal>
  )
}

// ─── Tracking link ─────────────────────────────────────────────────
function TrackingLinkModal({ open, onClose }: { open: boolean; onClose: () => void }) {
  const [campaign, setCampaign] = useState('')
  const [source, setSource] = useState('facebook')
  const [copied, setCopied] = useState(false)

  const slug = campaign.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '') || 'campaign'
  const link = `https://track.bon8.co/${source}/${slug}-${Math.random().toString(36).slice(2, 8)}`

  function handleCopy() {
    navigator.clipboard.writeText(link).catch(() => {})
    setCopied(true)
    setTimeout(() => setCopied(false), 1500)
  }

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="สร้าง Tracking Link"
      size="md"
      footer={
        <>
          <Button variant="glass-dark" onClick={onClose}>ปิด</Button>
          <Button leadingIcon={copied ? <Check size={16} /> : <Copy size={16} />} onClick={handleCopy}>
            {copied ? 'คัดลอกแล้ว' : 'คัดลอกลิงก์'}
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-4">
        <Input
          label="ชื่อแคมเปญ"
          placeholder="เช่น Songkran Promo 2025"
          value={campaign}
          onChange={(e) => setCampaign(e.target.value)}
        />
        <label className="flex flex-col gap-2">
          <span className="font-450 text-body-sm text-black/70">ช่องทาง</span>
          <select
            className="rounded-lg border border-black/[0.08] bg-white px-3 py-2 text-body font-330 outline-none focus:border-black/40 transition-colors"
            value={source}
            onChange={(e) => setSource(e.target.value)}
          >
            <option value="facebook">Facebook Ads</option>
            <option value="google">Google Ads</option>
            <option value="line">LINE OA</option>
            <option value="tiktok">TikTok Ads</option>
            <option value="instagram">Instagram</option>
            <option value="other">อื่นๆ</option>
          </select>
        </label>
        <div className="rounded-lg bg-black/[0.03] border border-black/[0.06] p-4 flex items-center gap-3">
          <Link2 size={18} className="text-black/55 shrink-0" />
          <code className="font-mono text-body-sm text-black/80 truncate flex-1">{link}</code>
        </div>
      </div>
    </Modal>
  )
}

// ─── Upload data ───────────────────────────────────────────────────
function UploadDataModal({ open, onClose }: { open: boolean; onClose: () => void }) {
  const [tab, setTab] = useState<'file' | 'source'>('file')

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Upload data"
      size="lg"
      footer={
        <>
          <Button variant="glass-dark" onClick={onClose}>ยกเลิก</Button>
          <Button onClick={onClose}>นำเข้าข้อมูล</Button>
        </>
      }
    >
      <div className="flex flex-col gap-5">
        <div className="flex gap-1 p-1 bg-black/[0.04] rounded-pill self-start">
          <button
            onClick={() => setTab('file')}
            className={`px-4 py-1.5 rounded-pill text-body-sm font-450 transition-colors ${
              tab === 'file' ? 'bg-white text-black shadow-soft' : 'text-black/60 hover:text-black'
            }`}
          >
            Upload CSV / XLSX
          </button>
          <button
            onClick={() => setTab('source')}
            className={`px-4 py-1.5 rounded-pill text-body-sm font-450 transition-colors ${
              tab === 'source' ? 'bg-white text-black shadow-soft' : 'text-black/60 hover:text-black'
            }`}
          >
            Source link (FB API)
          </button>
        </div>

        {tab === 'file' ? (
          <div className="rounded-xl border-2 border-dashed border-black/[0.12] p-10 flex flex-col items-center gap-3 text-center">
            <FileSpreadsheet size={32} className="text-black/40" />
            <p className="font-450 text-body text-black/80">ลากไฟล์มาวาง หรือคลิกเพื่อเลือก</p>
            <p className="font-330 text-body-sm text-black/50">รองรับ .csv, .xlsx ขนาดไม่เกิน 25 MB</p>
            <Button size="sm" variant="glass-dark" leadingIcon={<Upload size={14} />}>
              เลือกไฟล์
            </Button>
          </div>
        ) : (
          <div className="flex flex-col gap-4">
            <Input label="Source name" placeholder="เช่น CR Heart — FB Ads main" />
            <Input label="Facebook Ad Account ID" placeholder="act_1234567890" />
            <label className="flex flex-col gap-2">
              <span className="font-450 text-body-sm text-black/70">Sync mode</span>
              <select className="rounded-lg border border-black/[0.08] bg-white px-3 py-2 text-body font-330 outline-none focus:border-black/40 transition-colors">
                <option>Auto sync ทุก 1 ชม.</option>
                <option>Auto sync ทุก 6 ชม.</option>
                <option>Manual sync</option>
              </select>
            </label>
          </div>
        )}
      </div>
    </Modal>
  )
}
